import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ProgressSize } from '@freelancer/ui/progress-bar';
import { textVariants } from '@freelancer/ui/text';
import { TextSize } from '@freelancer/ui/text';
import { AcceptRateSize } from './accept-rate.types';

@Component({
  standalone: false,
  selector: 'fl-accept-rate',
  template: `
    <div
      class="AcceptRateContainer"
      [class.desktop:hide]="compact"
      [class.mobile:hide]="compact || !!mobileCompact"
      [class.tablet:hide]="compact"
    >
      <fl-icon
        class="HandshakeIcon mr-xxxsmall"
        [color]="IconColor.SECONDARY"
        [name]="'ui-handshake'"
        [useIconFont]="true"
        [size]="size"
        [sizeDesktop]="sizeDesktop"
        [sizeTablet]="sizeTablet"
      ></fl-icon>

      <div
        class="AcceptRateProgressBarContainer mr-xxxsmall"
        [class.desktop_large:show]="desktopSmallCompact"
      >
        @if (!compact) {
          <fl-progress-bar
            class="AcceptRateProgressBar"
            label="Accept Rate Progress Bar"
            i18n-label="Accept rate progress bar's label"
            [progressPercentage]="acceptRate * 100"
            [roundedCorners]="false"
            [size]="acceptRateBarSize"
            [sizeDesktop]="acceptRateBarSizeDesktop"
            [sizeTablet]="acceptRateBarSizeTablet"
          >
          </fl-progress-bar>
        }
      </div>
      <div class="RightAcceptRateText">
        <ng-container *ngTemplateOutlet="acceptRateText"></ng-container>
      </div>
    </div>

    <div
      class="AcceptRateContainer"
      [class.desktop:hide]="!compact"
      [class.mobile:hide]="!compact && !mobileCompact"
      [class.tablet:hide]="!compact"
    >
      <fl-icon
        class="HandshakeIcon mr-xxxsmall"
        [color]="IconColor.SECONDARY"
        [name]="'ui-handshake'"
        [useIconFont]="true"
        [size]="size"
        [sizeDesktop]="sizeDesktop"
        [sizeTablet]="sizeTablet"
      ></fl-icon>
      <ng-container *ngTemplateOutlet="acceptRateText"></ng-container>
    </div>
    <ng-template #acceptRateText>
      <p
        class="AcceptRateText {{ acceptRateTextClass }}"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
      >
        {{ acceptRate * 100 | number: '1.0-0' }}%
      </p>
    </ng-template>
  `,
  styleUrls: ['./accept-rate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AcceptRateComponent implements OnChanges, OnInit {
  IconColor = IconColor;

  acceptRateTextSize: TextSize = TextSize.BODY_16;
  acceptRateTextSizeTablet?: TextSize | undefined;
  acceptRateTextSizeDesktop?: TextSize | undefined;
  acceptRateTextClass: string;

  acceptRateBarSize: ProgressSize = ProgressSize.SMALL;
  acceptRateBarSizeTablet?: ProgressSize | undefined;
  acceptRateBarSizeDesktop?: ProgressSize | undefined;

  @Input() compact = false;
  /** Automatically sets compact on mobile */
  @Input() mobileCompact?: boolean;
  @Input() desktopSmallCompact?: boolean;
  @Input() acceptRate: number;

  @HostBinding('attr.data-size')
  @Input()
  size: AcceptRateSize = IconSize.LARGE;

  /** Change the [size] from tablet and above */
  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: AcceptRateSize;

  /** Change the [size] from desktop and above */
  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: AcceptRateSize;

  ngOnInit(): void {
    this.calculateSizes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.size || changes.sizeTablet || changes.sizeDesktop) {
      this.calculateSizes();
    }
  }

  private calculateSizes(): void {
    this.calculateTextAndBarSize(this.size, 'default');
    this.calculateTextAndBarSize(this.sizeTablet, 'tablet');
    this.calculateTextAndBarSize(this.sizeDesktop, 'desktop');
    this.acceptRateTextClass = textVariants({
      size: this.acceptRateTextSize,
      sizeTablet: this.acceptRateTextSizeTablet,
      sizeDesktop: this.acceptRateTextSizeDesktop,
    });
  }

  private calculateTextAndBarSize(
    size: AcceptRateSize | undefined,
    type: 'default' | 'tablet' | 'desktop',
  ): void {
    if (!size) {
      switch (type) {
        case 'tablet':
          this.acceptRateTextSizeTablet = undefined;
          this.acceptRateBarSizeTablet = undefined;
          break;
        case 'desktop':
          this.acceptRateTextSizeDesktop = undefined;
          this.acceptRateBarSizeDesktop = undefined;
          break;
        default:
          // No action needed for 'default' type when size is undefined
          break;
      }
      return;
    }

    switch (size) {
      case IconSize.SMALL:
        switch (type) {
          case 'default':
            this.acceptRateTextSize = TextSize.BODY_16;
            this.acceptRateBarSize = ProgressSize.LARGE;
            break;
          case 'tablet':
            this.acceptRateTextSizeTablet = TextSize.BODY_16;
            this.acceptRateBarSizeTablet = ProgressSize.LARGE;
            break;
          case 'desktop':
            this.acceptRateTextSizeDesktop = TextSize.BODY_16;
            this.acceptRateBarSizeDesktop = ProgressSize.LARGE;
            break;
          default:
            // No action needed for unexpected type
            break;
        }
        break;
      case IconSize.LARGE:
        switch (type) {
          case 'default':
            this.acceptRateTextSize = TextSize.BODY_16;
            this.acceptRateBarSize = ProgressSize.XLARGE;
            break;
          case 'tablet':
            this.acceptRateTextSizeTablet = TextSize.BODY_16;
            this.acceptRateBarSizeTablet = ProgressSize.XLARGE;
            break;
          case 'desktop':
            this.acceptRateTextSizeDesktop = TextSize.BODY_16;
            this.acceptRateBarSizeDesktop = ProgressSize.XLARGE;
            break;
          default:
            // No action needed for unexpected type
            break;
        }
        break;
      default:
        switch (type) {
          case 'tablet':
            this.acceptRateTextSizeTablet = undefined;
            this.acceptRateBarSizeTablet = undefined;
            break;
          case 'desktop':
            this.acceptRateTextSizeDesktop = undefined;
            this.acceptRateBarSizeDesktop = undefined;
            break;
          default:
            // No action needed for 'default' type in default case
            break;
        }
    }
  }
}
